/**
 * Europe/Oslo timezone helpers.
 * Single source of truth for all Oslo-time evaluations (quiet hours, daily reset).
 * Uses Intl.DateTimeFormat to correctly handle CET/CEST (DST).
 */

const osloFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Oslo',
  hour: 'numeric',
  hour12: false,
});

const osloDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Oslo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Get the current hour (0–23) in Europe/Oslo timezone.
 */
export function getOsloHour(nowMs?: number): number {
  const d = nowMs !== undefined ? new Date(nowMs) : new Date();
  const parts = osloFormatter.formatToParts(d);
  const hourPart = parts.find(p => p.type === 'hour');
  // Intl returns "24" for midnight in hour12=false, normalize to 0
  const h = parseInt(hourPart?.value ?? '0', 10);
  return h === 24 ? 0 : h;
}

/**
 * Get today's date string (YYYY-MM-DD) in Europe/Oslo timezone.
 */
export function getOsloDateStr(nowMs?: number): string {
  const d = nowMs !== undefined ? new Date(nowMs) : new Date();
  const parts = osloDateFormatter.formatToParts(d);
  const year = parts.find(p => p.type === 'year')?.value ?? '2025';
  const month = parts.find(p => p.type === 'month')?.value ?? '01';
  const day = parts.find(p => p.type === 'day')?.value ?? '01';
  return `${year}-${month}-${day}`;
}

/**
 * Check if current Oslo-time falls within quiet hours.
 * Handles wrap-around (e.g. 22–07 means 22:00 to 06:59).
 */
export function isQuietHoursOslo(nowMs: number, quietStart: number, quietEnd: number): boolean {
  const hour = getOsloHour(nowMs);
  if (quietStart > quietEnd) {
    // Wraps midnight: e.g. 22–07 → quiet if hour >= 22 OR hour < 7
    return hour >= quietStart || hour < quietEnd;
  }
  // Same day: e.g. 13–15 → quiet if 13 <= hour < 15
  return hour >= quietStart && hour < quietEnd;
}
